import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'

import { getBidIneligibilityReason } from '@/components/listings/ListView/listingHelpers'
import { getUserMinBidAmount } from '@/helpers/getUserMinBidAmount'
import { violatesBidPrecision } from '@/helpers/violatesBidPrecision'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import type { LotBidGroup } from './groupBidSheetRowsByLot'
import { groupBidSheetRowsByLot } from './groupBidSheetRowsByLot'
import { parsePlainDecimal } from './parseBidAmount'
import type {
  BidSheetListingDetails,
  ParsedBidSheetRow,
  ValidatedBidSheetRow,
} from './types'

// Shared by every branch below that has a resolved `listing` — a row's
// error code decides whether the bid is accepted, not whether the listing
// it names was found, so the review table's Title/Min Required Bid columns
// should read the same regardless of which error (if any) the row ends up
// reporting.
const buildListingDetails = (
  listing: CombinedListingSearchResult,
  mobileListingIds: ReadonlySet<string>
): BidSheetListingDetails => ({
  // Prefer the device model over the general title, but only on a
  // mobile-category-storefront lot — model is a generic, category-
  // unrestricted attribute, so substituting it for every listing that
  // happens to carry one would replace a non-mobile listing's title with
  // text the buyer never saw in the list view. model is a string[]
  // because a listing can span models.
  title:
    mobileListingIds.has(listing.listingId) &&
    listing.model &&
    listing.model.length > 0
      ? listing.model.join(', ')
      : listing.title,
  lotNumber: listing.sellerLotId?.[0] ?? null,
  currency: listing.currency,
  sellerCollects: listing.sellerCollects,
  storefrontName: listing.storefrontName,
  minimumRequiredBid: getUserMinBidAmount({
    bidAmount: listing.bidAmount,
    bidPrecision: listing.bidPrecision,
    nextMinBidAmount: listing.nextMinBidAmount,
    startPrice: listing.startPrice,
    winningBidAmount: listing.winningBidAmount,
  }),
})

// A 'duplicate' casualty still names a real, found listing (the conflict is
// over which row's bid wins, not whether the listing exists) — look it up
// so the review table can show its Title/Min Required Bid instead of a
// dash, same as every other error code that resolved a listing.
const duplicateRow = (
  row: ParsedBidSheetRow,
  listingsById: Map<string, CombinedListingSearchResult>,
  mobileListingIds: ReadonlySet<string> = new Set<string>()
): ValidatedBidSheetRow => {
  const listing = listingsById.get(row.listingId)
  return {
    ...row,
    status: 'error',
    errorCode: 'duplicate',
    listingDetails: listing
      ? buildListingDetails(listing, mobileListingIds)
      : null,
    // Still a genuine, unresolved casualty of the conflict (still holds its
    // own bid, or was itself dismissed) — never superseded.
    supersededLot: false,
  }
}

type ValidateGroupRowOptions = {
  acceptedPolicies?: Record<string, unknown> | null
  mobileListingIds?: ReadonlySet<string>
  supersededLot?: boolean
}

/**
 * Validates one row's own `bidSubmissionRaw` against the listings fetched
 * for this upload — not found, no active auction, already won, ended,
 * invalid amount, below minimum, or valid, in that order. `group` supplies
 * the per-unit rollup info a 'valid' result carries; it does not have to be
 * `row` itself — callers pass the group's resolved winner here as readily
 * as a sibling row validating purely on its own. `supersededLot` tags an
 * error result as not mattering because the lot already has a winner
 * elsewhere in the group — see `ValidatedBidSheetRow.supersededLot`.
 */
const validateGroupRow = (
  row: ParsedBidSheetRow,
  group: LotBidGroup,
  listingsById: Map<string, CombinedListingSearchResult>,
  {
    acceptedPolicies,
    mobileListingIds = new Set<string>(),
    supersededLot = false,
  }: ValidateGroupRowOptions = {}
): ValidatedBidSheetRow => {
  const listing = listingsById.get(row.listingId)
  if (!listing) {
    return {
      ...row,
      status: 'error',
      errorCode: 'notFound',
      listingDetails: null,
      supersededLot,
    }
  }

  const listingDetails = buildListingDetails(listing, mobileListingIds)

  // Checked ahead of everything else, same as ActionForm.tsx's own routing
  // to View Details instead of Bid Now: an Enterprise/Amazon-Europe listing
  // (non-3MP, carrying its own ENT system id in the top-level `auctionId`
  // field — see CombinedListingSearchResult.auctionId), an unaccepted-terms
  // or Make an Offer listing (the same SENTINEL condition useColumnDefs
  // already gates on), or a Contract listing all have no B-Stock auction
  // this review screen can place a bid against. Each reason gets its own
  // errorCode (rather than collapsing all four into 'noActiveAuction') so
  // the buyer sees which one actually applies and can act on it.
  // useBulkBidMutations pre-fails a null-auctionId listing the same way at
  // placement time (see its 'noActiveAuction' reasonCode) — that catches
  // the rarer case where a 3MP auction genuinely has no id yet; this
  // catches the rest. The listing was still found, so listingDetails
  // carries through same as any other error code below.
  const ineligibilityReason = getBidIneligibilityReason(
    listing,
    acceptedPolicies
  )
  if (ineligibilityReason) {
    return {
      ...row,
      status: 'error',
      errorCode: ineligibilityReason,
      listingDetails,
      supersededLot,
    }
  }

  // Checked ahead of closed/canceled — a listing can be WON and still
  // technically open, and "you already won this" is the more useful,
  // buyer-specific reason than a generic "ended" would be.
  if (listing.bidStatus === BidStatus.WON) {
    return {
      ...row,
      status: 'error',
      errorCode: 'alreadyWon',
      listingDetails,
      supersededLot,
    }
  }

  if (listing.closed || listing.canceled) {
    return {
      ...row,
      status: 'error',
      errorCode: 'ended',
      listingDetails,
      supersededLot,
    }
  }

  const amount = parsePlainDecimal(row.bidSubmissionRaw)
  if (
    amount === null ||
    amount <= 0 ||
    violatesBidPrecision(row.bidSubmissionRaw.trim(), listing.bidPrecision)
  ) {
    return {
      ...row,
      status: 'error',
      errorCode: 'invalidAmount',
      listingDetails,
      supersededLot,
    }
  }

  if (amount < listingDetails.minimumRequiredBid) {
    return {
      ...row,
      status: 'error',
      errorCode: 'belowMinimum',
      listingDetails,
      supersededLot,
    }
  }

  return {
    ...row,
    status: 'valid',
    amount,
    listingDetails,
    perUnitTotal: group.perUnitTotal,
    usedLotBidOverPerUnit: group.usedLotBidOverPerUnit,
  }
}

type ValidateBidSheetRowsOptions = {
  editedRowNumbers?: ReadonlySet<number>
  removedRowNumbers?: ReadonlySet<number>
  acceptedPolicies?: Record<string, unknown> | null
  mobileListingIds?: ReadonlySet<string>
}

/**
 * Validates each parsed row against the listings fetched for this upload
 * (see UploadBidSheetModal — fetched by ID, not the currently-displayed
 * page, since a bid sheet can span many pages of the List View). Rows are
 * first grouped by Listing ID (see `groupBidSheetRowsByLot`) — a manifest
 * lot's lines all roll up into one result — then validated per group: a
 * blank Listing ID reports 'missingListingId'; a genuine, unresolved
 * conflict (`isDuplicateLotBid`) reports each candidate row individually
 * instead of folding to one, so the buyer sees every still-competing (or
 * cleared-but-not-yet-resolved) bid; once a conflict resolves down to a
 * single active bid (`conflictResolved`), the winner validates normally,
 * a sibling still holding its own competing bid (or itself removed) reports
 * 'duplicate', and a sibling that's merely been cleared validates on its own
 * (typically invalidAmount) but tagged `supersededLot` — kept visible and
 * editable (a row is never silently dropped mid-edit), while every error
 * tally downstream treats it as inert since its lot already has a winner;
 * everything else validates its single representative row.
 */
export const validateBidSheetRows = (
  rows: ParsedBidSheetRow[],
  listingsById: Map<string, CombinedListingSearchResult>,
  {
    editedRowNumbers = new Set<number>(),
    removedRowNumbers = new Set<number>(),
    acceptedPolicies,
    mobileListingIds,
  }: ValidateBidSheetRowsOptions = {}
): ValidatedBidSheetRow[] => {
  return groupBidSheetRowsByLot(
    rows,
    editedRowNumbers,
    removedRowNumbers
  ).flatMap((group): ValidatedBidSheetRow[] => {
    // Checked ahead of the blank-Listing-ID branch below — an unreadable
    // row's listingId is blank too (parseBidSheetCsv never trusts a broken
    // line's fields), but it needs its own distinct reason rather than
    // reading as a buyer who simply left the column empty.
    if (group.representative.unreadable) {
      return [
        {
          ...group.representative,
          status: 'error',
          errorCode: 'unreadable',
          listingDetails: null,
          supersededLot: false,
        },
      ]
    }

    if (!group.representative.listingId) {
      return [
        {
          ...group.representative,
          status: 'error',
          errorCode: 'missingListingId',
          listingDetails: null,
          supersededLot: false,
        },
      ]
    }

    // Only the rows competing for a lot-level bid — a per-unit-only
    // sibling line has no bid of its own to conflict over, so it stays
    // folded away (as if this lot had no conflict at all) even while its
    // lot's real bids are in conflict.
    if (group.isDuplicateLotBid || group.conflictResolved) {
      return group.conflictingRows.flatMap((row): ValidatedBidSheetRow[] => {
        if (
          group.conflictResolved &&
          row.rowNumber === group.representative.rowNumber
        ) {
          return [
            validateGroupRow(
              { ...row, bidSubmissionRaw: group.bidSubmissionRaw },
              group,
              listingsById,
              { acceptedPolicies, mobileListingIds }
            ),
          ]
        }
        // Still holds its own competing bid, or is itself dismissed — a
        // genuine casualty of the conflict.
        if (
          row.bidSubmissionRaw !== '' ||
          removedRowNumbers.has(row.rowNumber)
        ) {
          return [duplicateRow(row, listingsById, mobileListingIds)]
        }
        // Merely cleared (not removed, no bid of its own right now). Always
        // kept visible and validated on its own — never folded away — so a
        // buyer mid-keystroke clearing this row to retype a fresh amount
        // never loses the input they're typing into. When the lot is
        // already resolved down to its winner above (conflictResolved),
        // this row's own invalidAmount error is tagged supersededLot so it
        // doesn't re-block Submit or show up as a real error downstream; in
        // an unresolved isDuplicateLotBid group (e.g. every candidate
        // cleared, none removed yet), there's no winner to defer to, so it
        // stays a genuine, blocking error.
        return [
          validateGroupRow(row, group, listingsById, {
            acceptedPolicies,
            mobileListingIds,
            supersededLot: group.conflictResolved,
          }),
        ]
      })
    }

    return [
      validateGroupRow(
        { ...group.representative, bidSubmissionRaw: group.bidSubmissionRaw },
        group,
        listingsById,
        { acceptedPolicies, mobileListingIds }
      ),
    ]
  })
}
